import { Roles } from '@omnea-digital/toolbox'
import { TFunction } from 'i18next'
import { assertNever } from '@omnea-app/legacy/utils/type-guard/assertNever'

export enum UserFacingRole {
  SUPPLIER = 'supplier',
  USER = 'user',
  VIEWER = 'viewer',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super-admin',
}

export interface UserRoleConfig {
  role: Roles
  hasConfigPermissions: boolean
  isViewOnly: boolean
}

export const isUserFacingRole = (role: string): role is UserFacingRole => {
  return Object.values(UserFacingRole).includes(role as UserFacingRole)
}

/* Translates backend role and config status into a frontend-friendly role label:
 * User       User
 * Supplier   Supplier
 * Viewer     Admin with view only restriction
 * Editor     Admin without config permission
 * Admin      Admin with config permission
 */

export const getUserFacingRoleFromUserConfig = ({ role, hasConfigPermissions, isViewOnly }: UserRoleConfig) => {
  switch (role) {
    case Roles.SUPPLIER:
      return UserFacingRole.SUPPLIER
    case Roles.USER:
      return UserFacingRole.USER
    case Roles.ADMIN:
      if (isViewOnly) {
        return UserFacingRole.VIEWER
      }
      if (hasConfigPermissions) {
        return UserFacingRole.SUPER_ADMIN
      }
      return UserFacingRole.ADMIN
    default:
      assertNever(role)
      return UserFacingRole.USER
  }
}

export const getUserFacingRoleLabel = ({
  t,
  role,
  p2pAdminMode = false,
}: {
  t: TFunction<'settings'>
  role: UserFacingRole
  /** P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): renders the Admin role as "P2P User". */
  p2pAdminMode?: boolean
}): string => {
  switch (role) {
    case UserFacingRole.SUPER_ADMIN:
      return t('users.role-super-admin')
    case UserFacingRole.ADMIN:
      return p2pAdminMode ? t('users.role-p2p-user') : t('users.role-admin')
    case UserFacingRole.VIEWER:
      return t('users.role-viewer')
    case UserFacingRole.USER:
      return t('users.role-user')
    case UserFacingRole.SUPPLIER:
      return t('users.role-supplier')
    default:
      assertNever(role)
      return t('role-label.unknown')
  }
}

export const getRoleLabelFromUserConfig = ({
  t,
  userConfig,
  p2pAdminMode = false,
}: {
  t: TFunction<'settings'>
  userConfig: UserRoleConfig
  p2pAdminMode?: boolean
}) => getUserFacingRoleLabel({ t, role: getUserFacingRoleFromUserConfig(userConfig), p2pAdminMode })

export const getUserConfigFromUserFacingRole = (role: UserFacingRole): UserRoleConfig => {
  switch (role) {
    case UserFacingRole.SUPER_ADMIN:
      return { role: Roles.ADMIN, hasConfigPermissions: true, isViewOnly: false }
    case UserFacingRole.ADMIN:
      return { role: Roles.ADMIN, hasConfigPermissions: false, isViewOnly: false }
    case UserFacingRole.VIEWER:
      return { role: Roles.ADMIN, hasConfigPermissions: false, isViewOnly: true }
    case UserFacingRole.USER:
      return { role: Roles.USER, hasConfigPermissions: false, isViewOnly: false }
    case UserFacingRole.SUPPLIER:
      return { role: Roles.SUPPLIER, hasConfigPermissions: false, isViewOnly: false }
    default:
      assertNever(role)
      throw new Error(`Invalid role: ${role}`)
  }
}
